import { HttpEventType } from "@angular/common/http";
import { Component, EventEmitter, Output } from "@angular/core";
import { FileRestrictions, SelectEvent } from "@progress/kendo-angular-upload";
import { UserService } from "src/app/admin/user/shared/services/user.service";
import { ProvisioningService } from "../shared/services/provisioning.service";
import { TranslationLoaderFacade } from "../../../localization/translation-loader.facade";
import { TranslateService } from "@ngx-translate/core";
import { Store } from "@ngrx/store";
import { AppState } from "src/app/shared/models/store models/app-state.interface";
import { combineLatest } from "rxjs";
import { getACLicenseStatus, getLTLicenseStatus } from "src/app/state";

@Component({
  selector: "app-data-import-selection",
  templateUrl: "./data-import-selection.component.html",
  styleUrls: ["./data-import-selection.component.scss"],
})
export class DataImportSelectionComponent {
  public selectableEntities = [
    { id: 2, sortorder: 1, name: "Users" },
    { id: 6, sortorder: 2, name: "User Availability" },
    { id: 8, sortorder: 3, name: "User Skills" },
    { id: 1, sortorder: 4, name: "Locations" },
    { id: 7, sortorder: 5, name: "Operating Hours" },
    { id: 5, sortorder: 6, name: "Holidays" },
    { id: 4, sortorder: 7, name: "Product Categories" },
    { id: 3, sortorder: 8, name: "Products & Services" },
  ];

  fileData?: File;
  fileTypeSelected: number = this.selectableEntities[0].id;
  restrictions: FileRestrictions = {
    allowedExtensions: [".csv", ".txt"],
  };
  submitted: boolean = false;
  invalidFile: boolean = false;
  ErrorLogs: string[] = [];
  Errors: string = "";

  @Output() fileChangeEvent = new EventEmitter();

  private readonly translationKeysById: Record<number, string> = {
    2: "_General_Users_Text_",
    6: "_Data_Import_User_Availability_Text_",
    8: "_Data_Import_User_Skills_Text_",
    1: "_General_Locations_Text_",
    7: "_Location_Operating_Hours_Tab_Title_Text_",
    5: "_Location_Holidays_Tab_Title_Text_",
    4: "_General_Product_Categories_Text_",
    3: "_General_Products_Services_Text_",
  };

  constructor(
    public userService: UserService,
    public provisioningService: ProvisioningService,
    private translationLoaderFacade: TranslationLoaderFacade,
    private translateService: TranslateService,
    private store: Store<AppState>,
  ) {}

  ngOnInit() {

    combineLatest([
      this.store.select(getLTLicenseStatus),
      this.store.select(getACLicenseStatus),
    ]).subscribe(([isLTLicenseEnabled, isACLicenseEnabled]) => {
      this.selectableEntities = this.getEntitiesForLicense(
        isLTLicenseEnabled,
        isACLicenseEnabled,
      );
      this.fileTypeSelected =
        this.selectableEntities[0]?.id ?? this.fileTypeSelected;
      this.setTranslatedNames();
      this.selectableEntities.sort((a, b) => a.sortorder - b.sortorder);
    });
  }

  private getEntitiesForLicense(isLT: boolean, isAC: boolean) {
    const all = [
      { id: 2, sortorder: 1, name: "Users" },
      { id: 6, sortorder: 2, name: "User Availability" },
      { id: 8, sortorder: 3, name: "User Skills" },
      { id: 1, sortorder: 4, name: "Locations" },
      { id: 7, sortorder: 5, name: "Operating Hours" },
      { id: 5, sortorder: 6, name: "Holidays" },
      { id: 4, sortorder: 7, name: "Product Categories" },
      { id: 3, sortorder: 8, name: "Products & Services" },
    ];
    // LT only -> Users, Locations, Product Categories, Products & Services
    if (isLT && !isAC) {
      return all
        .filter((e) => [2, 1, 4, 3].includes(e.id))
        .map((e, idx) => ({ ...e, sortorder: idx + 1 }));
    }
    // AC only or both AC & LT -> full list
    return all;
  }

  private setTranslatedNames() {
    const keys = Object.values(this.translationKeysById);
    this.translateService.get(keys).subscribe((res) => {
      this.selectableEntities = this.selectableEntities.map((e) => ({
        ...e,
        name: res[this.translationKeysById[e.id]] ?? e.name,
      }));
    });
  }

  public handleFileInput(fileInput: SelectEvent) {
    this.fileChangeEvent.emit("File change");
    this.fileData = fileInput.files[0].rawFile;

    if (fileInput.files[0].extension) {
      this.invalidFile = !this.restrictions.allowedExtensions?.includes(
        fileInput.files[0].extension,
      );
    }

    this.submitted = false;
  }

  onSubmit() {
    if (!this.fileData) {
      return;
    }

    const formData = new FormData();
    formData.append("file", this.fileData, this.fileData.name);

    this.provisioningService
      .passCSV(formData, this.fileTypeSelected)
      .subscribe((events) => {
        if (events.type == HttpEventType.UploadProgress) {
          console.log(
            "Upload progress: ",
            `${Math.round((events.loaded / events.total) * 100)}%`,
          );
        } else if (events.type === HttpEventType.Response) {
          this.submitted = true;
          if (Array.isArray(events.body)) {
            this.ErrorLogs = events.body;
          } else {
            this.Errors = events.body;
          }
        }
      });
  }
}
